import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useForm } from "react-hook-form";
import { Link } from 'react-router-dom';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "webrtc-adapter";
import { api } from '../../../api/apiService';
import { toast } from "react-toastify";

const schema = yup.object().shape({
    image: yup
        .string()
        .required("Image is required"),
});

function AutoAttendance() {
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [verifyUser, setVerifyUser] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmit = async () => {
        try {
            setLoading(true);
            if (!image) {
                toast.error("Error: Image is required.");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append("employee_image", image);
            const response = await api.post("identify-face", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success("Your attendance has been successfully submitted");
            setVerifyUser(response?.data?.data)
            reset();
            setImage(null);
            setIsCameraActive(false);
        } catch (error) {
            toast.error(error.data.message)
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isCameraActive) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [isCameraActive]);

    // Start the camera
    const startCamera = async () => {
        setIsCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Error accessing camera:", err);
        }
    };

    // Stop the camera
    const stopCamera = () => {
        const stream = videoRef.current?.srcObject;
        const tracks = stream?.getTracks();
        tracks?.forEach(track => track.stop());
        videoRef.current = null;
        setIsCameraActive(false);
    };

    // Capture
    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to a Blob (image file)
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], "attendance-image.png", { type: "image/png" });
                setImage(file);
                setValue("image", file);
            }
            stopCamera();
        }, "image/png");
    };

    const Avatar = (name) => {
        const initials = name
            ? name.split(" ").map(word => word[0].toUpperCase()).join("")
            : "NA";
        return (
            <div
                style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8%",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {initials}
            </div>
        );
    };

    return (
        <div className="container">
            <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8">
                    <div className="d-flex justify-content-center py-4">
                        <Link to="/" className="logo d-flex align-items-center w-auto">
                            <span className="d-none d-lg-block">Attendance</span>
                        </Link>
                    </div>
                    <div className="card pt-3">
                        <div className="card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="mb-3">
                                    <div className="mb-2">
                                        {isCameraActive ? (
                                            <div className='d-flex justify-content-center'>
                                                <video ref={videoRef} autoPlay style={{ width: "300px", height: "200px", objectFit: "cover" }} />
                                            </div>
                                        ) : (
                                            image ? (
                                                <div className='d-flex justify-content-center'>
                                                    <img src={URL.createObjectURL(image)} alt="user" width="300" height="200" />
                                                </div>
                                            ) : verifyUser && verifyUser.employee_image ? (
                                                <div className="d-flex flex-column align-items-center mb-3">
                                                    <div className="d-flex justify-content-between align-items-center w-75">
                                                        <strong className='me-2'>Attendance Marked
                                                            <i className="verified-icon bi bi-check-circle-fill text-success ms-1"></i>
                                                        </strong>
                                                        <span className="text-capitalize">{verifyUser.employee_name}</span>
                                                    </div>
                                                    {verifyUser.employee_image ? (
                                                        <img
                                                            src={verifyUser.employee_image}
                                                            alt="Verified User"
                                                            height={200}
                                                            width={300}
                                                            style={{ objectFit: "cover", marginTop: "10px" }}
                                                        />
                                                    ) : (
                                                        Avatar(verifyUser.employee_name)
                                                    )}
                                                </div>
                                            ) : (
                                                <div className='d-flex justify-content-center'>
                                                    <div className='d-flex justify-content-center align-items-center bg-light' style={{ width: "300px", border: "1px solid black", height: "200px" }}>
                                                        No Image
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-center gap-2 mt-3">
                                        {isCameraActive ? (
                                            <>
                                                <Button
                                                    className="btn btn-primary"
                                                    onClick={capturePhoto}
                                                >
                                                    Capture
                                                </Button>
                                                <Button
                                                    className="btn btn-secondary"
                                                    onClick={stopCamera}
                                                >
                                                    Close
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                className="btn btn-primary"
                                                onClick={startCamera}
                                            >
                                                Take Photo
                                            </Button>
                                        )}
                                    </div>
                                    <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

                                    {/* Display image error message */}
                                    {errors.image && (
                                        <div className="invalid-feedback d-block">
                                            {errors.image.message}
                                        </div>
                                    )}
                                </div>

                                <div className="text-center">
                                    <Button type="submit" className="btn btn-primary"
                                        disabled={loading || !image}>
                                        {loading ? (
                                            <>Submitting <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></>
                                        ) : (
                                            "Submit"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default AutoAttendance;
